import React from 'react';

export const BrandDemo = ({ colors }) => {
    const getColor = (index) => colors[index]?.value || colors[0]?.value || '#3b82f6';

    // Convert hex to RGB for transparency effects
    const hexToRgba = (hex, alpha = 1) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return hex;
        const r = parseInt(result[1], 16);
        const g = parseInt(result[2], 16);
        const b = parseInt(result[3], 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    return (
        <div className="space-y-8">
            <div className="text-center">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Brand Identity System</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Complete brand identity guidelines, assets, and applications using your color palette.
                </p>
            </div>

            {/* Logo System */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Logo Variations & Applications</h5>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Primary Logo Variations */}
                    <div>
                        <h6 className="text-sm font-medium mb-4 text-gray-700 dark:text-gray-300">Logo Variations</h6>
                        <div className="grid grid-cols-2 gap-4">
                            {/* Full Color Logo */}
                            <div className="text-center p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                                <div className="h-20 flex items-center justify-center mb-3">
                                    <svg width="80" height="60" viewBox="0 0 80 60">
                                        <rect x="10" y="15" width="25" height="25" rx="4" fill={getColor(0)} />
                                        <circle cx="50" cy="27.5" r="12" fill={getColor(1)} />
                                        <polygon points="45,40 55,40 50,50" fill={getColor(2)} />
                                        <text x="40" y="55" fontSize="8" fill={getColor(0)} textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="bold">BRAND</text>
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500">Full Color</p>
                            </div>

                            {/* Monochrome Logo */}
                            <div className="text-center p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                                <div className="h-20 flex items-center justify-center mb-3">
                                    <svg width="80" height="60" viewBox="0 0 80 60">
                                        <rect x="10" y="15" width="25" height="25" rx="4" fill="#333" />
                                        <circle cx="50" cy="27.5" r="12" fill="#666" />
                                        <polygon points="45,40 55,40 50,50" fill="#999" />
                                        <text x="40" y="55" fontSize="8" fill="#333" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="bold">BRAND</text>
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500">Monochrome</p>
                            </div>

                            {/* Icon Only */}
                            <div className="text-center p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                                <div className="h-20 flex items-center justify-center mb-3">
                                    <svg width="50" height="50" viewBox="0 0 50 50">
                                        <rect x="5" y="10" width="20" height="20" rx="3" fill={getColor(0)} />
                                        <circle cx="35" cy="20" r="10" fill={getColor(1)} />
                                        <polygon points="30,35 40,35 35,42" fill={getColor(2)} />
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-500">Icon Mark</p>
                            </div>

                            {/* Reverse Logo */}
                            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: getColor(0) }}>
                                <div className="h-20 flex items-center justify-center mb-3">
                                    <svg width="80" height="60" viewBox="0 0 80 60">
                                        <rect x="10" y="15" width="25" height="25" rx="4" fill="white" />
                                        <circle cx="50" cy="27.5" r="12" fill="rgba(255,255,255,0.8)" />
                                        <polygon points="45,40 55,40 50,50" fill="rgba(255,255,255,0.6)" />
                                        <text x="40" y="55" fontSize="8" fill="white" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="bold">BRAND</text>
                                    </svg>
                                </div>
                                <p className="text-xs text-white">Reverse</p>
                            </div>
                        </div>
                    </div>

                    {/* Logo Applications */}
                    <div>
                        <h6 className="text-sm font-medium mb-4 text-gray-700 dark:text-gray-300">Real-World Applications</h6>
                        <div className="space-y-4">
                            {/* Business Card */}
                            <div className="relative h-20 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                <div className="absolute inset-0 bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-700"></div>
                                <div className="absolute top-2 left-3">
                                    <svg width="30" height="20" viewBox="0 0 30 20">
                                        <rect x="2" y="5" width="10" height="10" rx="2" fill={getColor(0)} />
                                        <circle cx="20" cy="10" r="5" fill={getColor(1)} />
                                    </svg>
                                </div>
                                <div className="absolute bottom-2 right-3 text-xs text-gray-600 dark:text-gray-400">Business Card</div>
                            </div>

                            {/* App Icon */}
                            <div className="relative h-20 rounded-lg overflow-hidden" style={{ backgroundColor: hexToRgba(getColor(0), 0.1) }}>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-12 h-12 rounded-xl shadow-lg flex items-center justify-center" style={{ backgroundColor: getColor(0) }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24">
                                            <rect x="4" y="6" width="8" height="8" rx="2" fill="white" />
                                            <circle cx="16" cy="10" r="4" fill="rgba(255,255,255,0.8)" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="absolute bottom-2 right-3 text-xs text-gray-600 dark:text-gray-400">App Icon</div>
                            </div>

                            {/* Website Header */}
                            <div className="relative h-20 rounded-lg overflow-hidden bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600">
                                <div className="absolute top-3 left-3 flex items-center gap-2">
                                    <svg width="20" height="15" viewBox="0 0 20 15">
                                        <rect x="1" y="3" width="7" height="7" rx="1" fill={getColor(0)} />
                                        <circle cx="14" cy="6.5" r="3" fill={getColor(1)} />
                                    </svg>
                                    <span className="text-xs font-medium text-gray-900 dark:text-white">Company</span>
                                </div>
                                <div className="absolute top-3 right-3 flex gap-1">
                                    <div className="w-12 h-1 bg-gray-200 dark:bg-gray-600 rounded"></div>
                                    <div className="w-8 h-1 bg-gray-200 dark:bg-gray-600 rounded"></div>
                                    <div className="w-10 h-1 bg-gray-200 dark:bg-gray-600 rounded"></div>
                                </div>
                                <div className="absolute bottom-2 right-3 text-xs text-gray-600 dark:text-gray-400">Website</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Brand Color System */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Brand Color System</h5>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Primary Palette */}
                    <div>
                        <h6 className="text-sm font-medium mb-4 text-gray-700 dark:text-gray-300">Primary Palette</h6>
                        <div className="space-y-3">
                            {colors.slice(0, 3).map((color, index) => (
                                <div key={index} className="flex items-center gap-4">
                                    <div className="w-16 h-12 rounded-lg shadow-sm" style={{ backgroundColor: color.value }}></div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                {['Primary', 'Secondary', 'Accent'][index]}
                                            </span>
                                            <span className="text-xs font-mono text-gray-500">{color.value}</span>
                                        </div>
                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                            {[
                                                'Main brand color, logos, primary CTAs',
                                                'Supporting elements, hover states',
                                                'Highlights, notifications, emphasis'
                                            ][index]}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Extended Palette */}
                    <div>
                        <h6 className="text-sm font-medium mb-4 text-gray-700 dark:text-gray-300">Extended Palette</h6>
                        <div className="space-y-3">
                            {colors.slice(3, 6).map((color, index) => (
                                <div key={index} className="flex items-center gap-4">
                                    <div className="w-16 h-12 rounded-lg shadow-sm" style={{ backgroundColor: color.value }}></div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                {['Support', 'Neutral', 'Background'][index]}
                                            </span>
                                            <span className="text-xs font-mono text-gray-500">{color.value}</span>
                                        </div>
                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                            {[
                                                'Secondary actions, borders',
                                                'Text, subtle elements',
                                                'Page backgrounds, cards'
                                            ][index]}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Color Usage Guidelines */}
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                    <h6 className="text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">Usage Guidelines</h6>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="text-center p-4 rounded-lg" style={{ backgroundColor: hexToRgba(getColor(0), 0.1) }}>
                            <div className="w-8 h-8 mx-auto mb-2 rounded-full" style={{ backgroundColor: getColor(0) }}></div>
                            <p className="text-xs text-gray-600 dark:text-gray-400">Dominant color should occupy 60% of design space</p>
                        </div>
                        <div className="text-center p-4 rounded-lg" style={{ backgroundColor: hexToRgba(getColor(1), 0.1) }}>
                            <div className="w-8 h-8 mx-auto mb-2 rounded-full" style={{ backgroundColor: getColor(1) }}></div>
                            <p className="text-xs text-gray-600 dark:text-gray-400">Secondary color for 30% of design elements</p>
                        </div>
                        <div className="text-center p-4 rounded-lg" style={{ backgroundColor: hexToRgba(getColor(2), 0.1) }}>
                            <div className="w-8 h-8 mx-auto mb-2 rounded-full" style={{ backgroundColor: getColor(2) }}></div>
                            <p className="text-xs text-gray-600 dark:text-gray-400">Accent color sparingly for 10% emphasis</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Brand Typography */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Brand Typography</h5>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Headings */}
                    <div>
                        <h6 className="text-sm font-medium mb-4 text-gray-700 dark:text-gray-300">Heading Hierarchy</h6>
                        <div className="space-y-4">
                            <div>
                                <h1 className="text-3xl font-bold mb-1" style={{ color: getColor(0) }}>
                                    Brand Headline
                                </h1>
                                <p className="text-xs text-gray-500">H1 - 30px Bold, Primary Color</p>
                            </div>
                            <div>
                                <h2 className="text-2xl font-semibold mb-1" style={{ color: getColor(1) }}>
                                    Section Title
                                </h2>
                                <p className="text-xs text-gray-500">H2 - 24px Semibold, Secondary Color</p>
                            </div>
                            <div>
                                <h3 className="text-xl font-medium mb-1" style={{ color: getColor(2) }}>
                                    Subsection
                                </h3>
                                <p className="text-xs text-gray-500">H3 - 20px Medium, Accent Color</p>
                            </div>
                        </div>
                    </div>

                    {/* Body Text & Applications */}
                    <div>
                        <h6 className="text-sm font-medium mb-4 text-gray-700 dark:text-gray-300">Body Text & Applications</h6>
                        <div className="space-y-4">
                            <div>
                                <p className="text-base mb-1 text-gray-900 dark:text-white">
                                    Primary body text for main content areas and descriptions.
                                </p>
                                <p className="text-xs text-gray-500">Body - 16px Regular, Primary Text Color</p>
                            </div>
                            <div>
                                <p className="text-sm mb-1 text-gray-600 dark:text-gray-400">
                                    Secondary text for captions, metadata, and supporting information.
                                </p>
                                <p className="text-xs text-gray-500">Small - 14px Regular, Secondary Text</p>
                            </div>
                            <div>
                                <button className="px-4 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: getColor(0) }}>
                                    Call to Action
                                </button>
                                <p className="text-xs text-gray-500 mt-1">Button - 14px Medium, White on Primary</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Marketing Materials */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Marketing Applications</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Business Card Design */}
                    <div>
                        <h6 className="text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">Business Card</h6>
                        <div className="relative h-32 rounded-lg overflow-hidden shadow-md">
                            <div className="absolute inset-0" style={{ backgroundColor: getColor(0) }}></div>
                            <div className="absolute top-0 right-0 w-16 h-full" style={{ backgroundColor: getColor(1), opacity: 0.3 }}></div>
                            <div className="absolute top-3 left-3 text-white">
                                <svg width="24" height="18" viewBox="0 0 24 18">
                                    <rect x="2" y="4" width="8" height="8" rx="2" fill="white" />
                                    <circle cx="16" cy="8" r="4" fill="rgba(255,255,255,0.8)" />
                                </svg>
                            </div>
                            <div className="absolute bottom-3 left-3 text-white">
                                <p className="text-xs font-medium">John Smith</p>
                                <p className="text-xs opacity-80">Creative Director</p>
                            </div>
                        </div>
                    </div>

                    {/* Letterhead */}
                    <div>
                        <h6 className="text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">Letterhead</h6>
                        <div className="relative h-32 rounded-lg overflow-hidden shadow-md bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                            <div className="absolute top-0 left-0 right-0 h-2" style={{ backgroundColor: getColor(0) }}></div>
                            <div className="absolute top-4 left-4 flex items-center gap-2">
                                <svg width="20" height="15" viewBox="0 0 20 15">
                                    <rect x="1" y="3" width="6" height="6" rx="1" fill={getColor(0)} />
                                    <circle cx="13" cy="6" r="3" fill={getColor(1)} />
                                </svg>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">Company Inc.</span>
                            </div>
                            <div className="absolute bottom-4 left-4 right-4">
                                <div className="space-y-1">
                                    <div className="h-1 bg-gray-200 dark:bg-gray-600 rounded w-full"></div>
                                    <div className="h-1 bg-gray-200 dark:bg-gray-600 rounded w-3/4"></div>
                                    <div className="h-1 bg-gray-200 dark:bg-gray-600 rounded w-1/2"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Presentation Cover */}
                    <div>
                        <h6 className="text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">Presentation</h6>
                        <div className="relative h-32 rounded-lg overflow-hidden shadow-md">
                            <div
                                className="absolute inset-0"
                                style={{
                                    background: `linear-gradient(135deg, ${getColor(0)} 0%, ${getColor(1)} 100%)`
                                }}
                            ></div>
                            <div className="absolute inset-0 flex items-center justify-center text-white">
                                <div className="text-center">
                                    <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-white/20 flex items-center justify-center">
                                        <svg width="16" height="12" viewBox="0 0 16 12">
                                            <rect x="1" y="2" width="5" height="5" rx="1" fill="white" />
                                            <circle cx="11" cy="4.5" r="2.5" fill="rgba(255,255,255,0.8)" />
                                        </svg>
                                    </div>
                                    <p className="text-xs font-medium">Brand Guidelines</p>
                                    <p className="text-xs opacity-80">Version 2.0</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Social Media Templates */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Social Media Templates</h5>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Instagram Post */}
                    <div>
                        <h6 className="text-xs font-medium mb-2 text-gray-700 dark:text-gray-300">Instagram Post</h6>
                        <div className="aspect-square rounded-lg overflow-hidden shadow-sm">
                            <div
                                className="h-full relative"
                                style={{
                                    background: `linear-gradient(45deg, ${hexToRgba(getColor(0), 0.9)} 0%, ${hexToRgba(getColor(1), 0.7)} 100%)`
                                }}
                            >
                                <div className="absolute top-3 left-3">
                                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                                        <svg width="12" height="9" viewBox="0 0 12 9">
                                            <rect x="1" y="2" width="4" height="4" rx="1" fill="white" />
                                            <circle cx="8" cy="4" r="2" fill="rgba(255,255,255,0.8)" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="absolute bottom-3 left-3 right-3 text-white">
                                    <p className="text-xs font-medium">New Collection</p>
                                    <p className="text-xs opacity-80">#brandstyle</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* LinkedIn Banner */}
                    <div>
                        <h6 className="text-xs font-medium mb-2 text-gray-700 dark:text-gray-300">LinkedIn Banner</h6>
                        <div className="aspect-[3/1] rounded-lg overflow-hidden shadow-sm">
                            <div
                                className="h-full relative"
                                style={{ backgroundColor: getColor(0) }}
                            >
                                <div className="absolute inset-0 flex items-center justify-between px-4">
                                    <div className="text-white">
                                        <p className="text-xs font-medium">Professional Services</p>
                                    </div>
                                    <div className="w-8 h-6 bg-white/20 rounded flex items-center justify-center">
                                        <svg width="16" height="12" viewBox="0 0 16 12">
                                            <rect x="1" y="2" width="5" height="5" rx="1" fill="white" />
                                            <circle cx="11" cy="4.5" r="2.5" fill="rgba(255,255,255,0.8)" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Twitter Header */}
                    <div>
                        <h6 className="text-xs font-medium mb-2 text-gray-700 dark:text-gray-300">Twitter Header</h6>
                        <div className="aspect-[3/1] rounded-lg overflow-hidden shadow-sm">
                            <div
                                className="h-full relative"
                                style={{
                                    background: `linear-gradient(90deg, ${getColor(1)} 0%, ${getColor(2)} 100%)`
                                }}
                            >
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="text-center text-white">
                                        <div className="w-6 h-6 mx-auto mb-1 rounded bg-white/30 flex items-center justify-center">
                                            <svg width="12" height="9" viewBox="0 0 12 9">
                                                <circle cx="6" cy="4.5" r="3" fill="white" />
                                            </svg>
                                        </div>
                                        <p className="text-xs">@brandname</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Story Template */}
                    <div>
                        <h6 className="text-xs font-medium mb-2 text-gray-700 dark:text-gray-300">Story Template</h6>
                        <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-sm">
                            <div
                                className="h-full relative"
                                style={{
                                    background: `radial-gradient(circle at center, ${hexToRgba(getColor(2), 0.8)} 0%, ${getColor(0)} 100%)`
                                }}
                            >
                                <div className="absolute top-4 left-1/2 transform -translate-x-1/2">
                                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                                        <svg width="16" height="12" viewBox="0 0 16 12">
                                            <rect x="3" y="3" width="4" height="4" rx="1" fill="white" />
                                            <circle cx="10" cy="5" r="2" fill="rgba(255,255,255,0.8)" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="absolute bottom-6 left-3 right-3 text-white text-center">
                                    <p className="text-xs font-medium">Swipe Up</p>
                                    <p className="text-xs opacity-80">Learn More</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Brand Guidelines Summary */}
            <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-4 text-gray-900 dark:text-white">Brand Guidelines Summary</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                    <div>
                        <h6 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Logo Usage</h6>
                        <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                            <li>• Maintain minimum clear space of 2x logo height</li>
                            <li>• Never stretch, rotate, or alter proportions</li>
                            <li>• Use appropriate version for each medium</li>
                            <li>• Ensure sufficient contrast on backgrounds</li>
                        </ul>
                    </div>
                    <div>
                        <h6 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Color Application</h6>
                        <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                            <li>• Primary color for main brand elements</li>
                            <li>• Secondary for supporting content</li>
                            <li>• Accent color sparingly for emphasis</li>
                            <li>• Maintain 4.5:1 contrast ratio minimum</li>
                        </ul>
                    </div>
                    <div>
                        <h6 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Typography Rules</h6>
                        <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                            <li>• Consistent font family across all materials</li>
                            <li>• Proper hierarchy for information structure</li>
                            <li>• Adequate line spacing for readability</li>
                            <li>• Color-coded text for brand recognition</li>
                        </ul>
                    </div>
                </div>

                {/* Brand Personality */}
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                    <h6 className="font-medium text-gray-800 dark:text-gray-200 mb-3">Brand Personality</h6>
                    <div className="flex flex-wrap gap-2">
                        {[
                            'Professional', 'Innovative', 'Trustworthy', 'Modern',
                            'Creative', 'Reliable', 'Dynamic', 'Approachable'
                        ].map((trait, index) => (
                            <span
                                key={trait}
                                className="px-3 py-1 rounded-full text-xs font-medium"
                                style={{
                                    backgroundColor: hexToRgba(getColor(index % colors.length), 0.1),
                                    color: getColor(index % colors.length)
                                }}
                            >
                                {trait}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};